import { Button, CircularProgress, Tab, Tabs, TextField } from '@mui/material';
import ObjectSelectBox from 'components/boss/ObjectSelectBox';
import BarChart from 'components/ui/charts/BarChart';
import LineChart from 'components/ui/charts/LineChart';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useTopPanel from 'hooks/useTopPanel';
import moment from 'moment';
import { memo, useEffect, useMemo, useState } from 'react';
import { numericFormatter } from 'react-number-format';
import { useQuery } from 'react-query';

/**
 * BI Analitika — Boss 8-menyu.
 * Tablar: Sotuv | To'lov | Voronka | Manba ROI | Prognoz.
 * Umumiy filtrlar (sana + obyekt) tepada; har tab o'z endpointini oladi.
 */
const money = (v) =>
  numericFormatter(String(Math.round(Number(v) || 0)), {
    thousandSeparator: ' ',
  });

const TABS = [
  { key: 'sales', label: 'Sotuv' },
  { key: 'payments', label: "To'lov" },
  { key: 'funnel', label: 'Voronka' },
  { key: 'leadsrc', label: 'Manba ROI' },
  { key: 'forecast', label: 'Prognoz' },
  { key: 'budget', label: 'Kutilmalar' },
];

const BiAnalytic = () => {
  const { setComponent } = useTopPanel();
  const [tab, setTab] = useState('sales');
  const [from, setFrom] = useState(
    moment().startOf('year').format('YYYY-MM-DD'),
  );
  const [till, setTill] = useState(moment().format('YYYY-MM-DD'));
  const [objectId, setObjectId] = useState('ALL');

  useEffect(() => {
    setComponent(<div className='component-title'>BI Analitika</div>);
  }, []);

  const filters = { from, till, objectId };

  return (
    <div className='p-3'>
      <div className='flex flex-wrap items-center gap-3 mb-3'>
        <TextField
          type='date'
          size='small'
          label='Dan'
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          type='date'
          size='small'
          label='Gacha'
          value={till}
          onChange={(e) => setTill(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <div className='w-56'>
          <ObjectSelectBox
            size='small'
            value={objectId}
            setValue={setObjectId}
          />
        </div>
      </div>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant='scrollable'
        className='mb-3 border-b border-line'
      >
        {TABS.map((tb) => (
          <Tab key={tb.key} value={tb.key} label={tb.label} />
        ))}
      </Tabs>

      {tab === 'sales' && <SalesTab {...filters} />}
      {tab === 'payments' && <PaymentsTab {...filters} />}
      {tab === 'funnel' && <FunnelTab {...filters} />}
      {tab === 'leadsrc' && <LeadSourcesTab {...filters} />}
      {tab === 'forecast' && <ForecastTab {...filters} />}
      {tab === 'budget' && <BudgetTab {...filters} />}
    </div>
  );
};

// ── umumiy: filtrlardan query paramlari ──
const useParams = (from, till, objectId, extra = {}) =>
  useMemo(() => {
    const p = new URLSearchParams({ from, till, ...extra });
    if (objectId && objectId !== 'ALL') p.set('object_id', objectId);
    return p.toString();
  }, [from, till, objectId, JSON.stringify(extra)]);

// ═══════════ 1) SOTUV ═══════════
const SalesTab = ({ from, till, objectId }) => {
  const axiosPrivate = useAxiosPrivate();
  const [blockId, setBlockId] = useState(null);
  const params = useParams(from, till, objectId, blockId ? { block_id: blockId } : {});
  useEffect(() => setBlockId(null), [objectId, from, till]);

  const { data, isFetching } = useQuery({
    queryKey: ['bi/sales', params],
    queryFn: async () => (await axiosPrivate.get(`/boss/bi/sales?${params}`)).data.data,
    keepPreviousData: true,
    retry: false,
  });

  const dyn = data?.dynamics || [];
  const byObject = data?.by_object || [];
  const totals = data?.totals || {};
  const drill = data?.drill;

  return (
    <div>
      {isFetching && <Loading />}
      <div className='grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 mb-4'>
        <Stat label='Shartnomalar' value={totals?.contracts_count ?? 0} />
        <Stat label="Summa (so'm)" value={money(totals?.sum_uzs)} />
        <Stat label='Summa ($)' value={money(totals?.sum_valute)} />
        {/* Yig'ilgan — tanlangan davrda haqiqiy tushgan to'lovlar */}
        <Stat label="Yig'ilgan (so'm)" value={money(totals?.paid_uzs)} />
        <Stat label="Yig'ilgan ($)" value={money(totals?.paid_valute)} />
        <Stat label='Maydon (m²)' value={money(totals?.square_sum)} />
      </div>

      <Card title={`Sotuv dinamikasi (${data?.group === 'month' ? 'oylik' : 'kunlik'})`}>
        {dyn.length ? (
          <LineChart
            chartLabels={dyn.map((d) =>
              data?.group === 'month'
                ? moment(d.period, 'YYYY-MM').format('MM.YYYY')
                : moment(d.period, 'YYYY-MM-DD').format('DD.MM'),
            )}
            chartDatasets={[
              {
                label: "Summa (so'm)",
                data: dyn.map((d) => Number(d.sum_uzs) || 0),
                borderColor: '#0057FF',
                backgroundColor: '#0057FF',
                tension: 0.3,
                hidden: !dyn.some((d) => Number(d.sum_uzs) > 0),
              },
              {
                label: 'Summa ($)',
                data: dyn.map((d) => Number(d.sum_valute) || 0),
                borderColor: '#f59e0b',
                backgroundColor: '#f59e0b',
                tension: 0.3,
                hidden: !dyn.some((d) => Number(d.sum_valute) > 0),
              },
              {
                label: 'Shartnoma soni',
                data: dyn.map((d) => Number(d.contracts_count) || 0),
                borderColor: '#16a34a',
                backgroundColor: '#16a34a',
                tension: 0.3,
                yAxisID: 'y1',
              },
            ]}
            optionPlugins={{
              legend: {
                display: true,
                position: 'bottom',
                labels: { usePointStyle: true, pointStyle: 'line' },
              },
            }}
            options={{
              scales: {
                y: { beginAtZero: true, position: 'left' },
                y1: {
                  beginAtZero: true,
                  position: 'right',
                  grid: { drawOnChartArea: false },
                  ticks: { precision: 0 },
                },
              },
            }}
          />
        ) : (
          <Empty />
        )}
      </Card>

      <div className='grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4'>
        <Card title="Obyekt bo'yicha (ustunni bosing → drill)">
          {byObject.length ? (
            <BarChart
              chartLabels={byObject.map((o) => o.object_name || '—')}
              chartDatasets={[
                {
                  label: "Summa (so'm)",
                  data: byObject.map((o) => Number(o.sum_uzs) || 0),
                  backgroundColor: '#0057FF',
                  borderRadius: 4,
                  hidden: !byObject.some((o) => Number(o.sum_uzs) > 0),
                },
                {
                  label: 'Summa ($)',
                  data: byObject.map((o) => Number(o.sum_valute) || 0),
                  backgroundColor: '#f59e0b',
                  borderRadius: 4,
                  hidden: !byObject.some((o) => Number(o.sum_valute) > 0),
                },
                // Yig'ilgan — davr ichida haqiqiy tushgan to'lovlar
                {
                  label: "Yig'ilgan (so'm)",
                  data: byObject.map((o) => Number(o.paid_uzs) || 0),
                  backgroundColor: '#16a34a',
                  borderRadius: 4,
                  hidden: !byObject.some((o) => Number(o.paid_uzs) > 0),
                },
                {
                  label: "Yig'ilgan ($)",
                  data: byObject.map((o) => Number(o.paid_valute) || 0),
                  backgroundColor: '#22c55e',
                  borderRadius: 4,
                  hidden: !byObject.some((o) => Number(o.paid_valute) > 0),
                },
              ]}
              optionPlugins={{
                legend: {
                  display: true,
                  position: 'bottom',
                  labels: { usePointStyle: true },
                },
              }}
              onClick={(els) => {
                if (els?.length) {
                  const o = byObject[els[0].index];
                  if (o?.object_id) setBlockId(null); // obyektni tepadagi filterda tanlang
                }
              }}
              options={{ scales: { y: { beginAtZero: true } } }}
            />
          ) : (
            <Empty />
          )}
        </Card>

        <Card
          title={
            drill?.level === 'stage'
              ? 'Qavatlar'
              : drill?.level === 'block'
                ? 'Bloklar (bosing → qavatlar)'
                : 'Drill — obyekt tanlang'
          }
        >
          {drill?.rows?.length ? (
            <SimpleTable
              head={[
                drill.level === 'stage' ? 'Qavat' : 'Blok',
                'Shartnoma',
                'Summa',
                "Yig'ilgan",
              ]}
              rows={drill.rows.map((r) => ({
                onClick:
                  drill.level === 'block' && r.block_id
                    ? () => setBlockId(r.block_id)
                    : null,
                cells: [
                  drill.level === 'stage' ? `${r.stage}-qavat` : r.block_name,
                  r.contracts_count,
                  <MoneyCell uzs={r.sum_uzs} usd={r.sum_valute} />,
                  // Yig'ilgan — shu blok/qavatdan davr ichida tushgan to'lovlar
                  <MoneyCell uzs={r.paid_uzs} usd={r.paid_valute} />,
                ],
              }))}
            />
          ) : (
            <Empty text='Yuqorida obyekt tanlang' />
          )}
        </Card>
      </div>
    </div>
  );
};

// ═══════════ 2) TO'LOV ═══════════
const PaymentsTab = ({ from, till, objectId }) => {
  const axiosPrivate = useAxiosPrivate();
  const params = useParams(from, till, objectId);
  const { data, isFetching } = useQuery({
    queryKey: ['bi/payments', params],
    queryFn: async () => (await axiosPrivate.get(`/boss/bi/payments?${params}`)).data.data,
    keepPreviousData: true,
    retry: false,
  });
  const expected = data?.expected || [];
  const labels = expected.map((e) =>
    e.ym ? moment(e.ym, 'YYYY-MM').format('MM.YYYY') : e.label,
  );
  const hasValute = expected.some((e) => Number(e.valute) > 0);

  return (
    <div>
      {isFetching && <Loading />}
      <Card title="Kutilayotgan tushum — so'm (oy bo'yicha)">
        {expected.length ? (
          <BarChart
            chartLabels={labels}
            chartDatasets={[
              {
                label: "Kutilmoqda (so'm)",
                data: expected.map((e) => Number(e.sum) || 0),
                backgroundColor: expected.map((e, i) =>
                  i === 0 ? '#dc2626' : '#0057FF',
                ),
                borderRadius: 4,
              },
            ]}
            optionPlugins={{
              legend: { display: false },
            }}
            options={{ scales: { y: { beginAtZero: true } } }}
          />
        ) : (
          <Empty />
        )}
      </Card>

      {hasValute && (
        <Card title="Kutilayotgan tushum — $ (oy bo'yicha)" className='mt-4'>
          <BarChart
            chartLabels={labels}
            chartDatasets={[
              {
                label: 'Kutilmoqda ($)',
                data: expected.map((e) => Number(e.valute) || 0),
                backgroundColor: expected.map((e, i) =>
                  i === 0 ? '#dc2626' : '#16a34a',
                ),
                borderRadius: 4,
              },
            ]}
            optionPlugins={{ legend: { display: false } }}
            options={{ scales: { y: { beginAtZero: true } } }}
          />
        </Card>
      )}

      <div className='text-[12px] text-muted mt-1'>
        <span className='inline-block w-3 h-3 rounded-sm align-middle mr-1' style={{ background: '#dc2626' }} />
        Muddati o'tgan &nbsp;·&nbsp;
        <span className='inline-block w-3 h-3 rounded-sm align-middle mr-1' style={{ background: '#0057FF' }} />
        Joriy/keyingi oylar
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4'>
        <Card title='Qarzdorlik reytingi (top 50)'>
          <SimpleTable
            head={['Shartnoma', 'Mijoz', 'Telefon', 'Qoldiq']}
            rows={(data?.debtors || []).map((d) => ({
              cells: [
                d.name,
                d.client,
                d.phone,
                money(d.left) + (d.isvalute == 1 ? ' $' : " so'm"),
              ],
            }))}
          />
        </Card>
        <Card title="Muddati o'tgan to'lovlar">
          <SimpleTable
            head={['Shartnoma', 'Mijoz', 'Sana', 'Kun', 'Qoldiq']}
            rows={(data?.overdue || []).map((o) => ({
              cells: [
                o.name,
                o.client,
                moment(o.payment_date).format('DD.MM.YYYY'),
                o.overdue_days,
                money(o.left),
              ],
            }))}
          />
        </Card>
      </div>
    </div>
  );
};

// ═══════════ 3) VORONKA ═══════════
const FunnelTab = ({ from, till, objectId }) => {
  const axiosPrivate = useAxiosPrivate();
  const params = useParams(from, till, objectId);
  const { data, isFetching } = useQuery({
    queryKey: ['bi/funnel', params],
    queryFn: async () => (await axiosPrivate.get(`/boss/bi/funnel?${params}`)).data.data,
    keepPreviousData: true,
    retry: false,
  });
  const stages = data?.stages || [];
  return (
    <div>
      {isFetching && <Loading />}
      <div className='grid grid-cols-3 gap-3 mb-4'>
        <Stat label='Jami lid' value={data?.total ?? 0} />
        <Stat label='Yutuq (shartnoma)' value={data?.won ?? 0} />
        <Stat label='Konversiya' value={(data?.conversion ?? 0) + '%'} />
      </div>
      <Card title='Voronka bosqichlari'>
        {stages.length ? (
          <BarChart
            chartLabels={stages.map((s) => s.stage || '—')}
            chartDatasets={[
              {
                label: 'Lidlar',
                data: stages.map((s) => Number(s.count) || 0),
                backgroundColor: '#0057FF',
                borderRadius: 4,
              },
            ]}
            options={{ indexAxis: 'y', scales: { x: { beginAtZero: true } } }}
          />
        ) : (
          <Empty />
        )}
      </Card>
      <Card title="Menejer bo'yicha solishtirma" className='mt-4'>
        <SimpleTable
          head={['Menejer', 'Lidlar', 'Yutuq', 'Sifatsiz', 'Konversiya']}
          rows={(data?.by_manager || []).map((m) => ({
            cells: [m.manager, m.total, m.won, m.trash, m.conversion + '%'],
          }))}
        />
      </Card>
    </div>
  );
};

// ═══════════ 4) MANBA ROI ═══════════
const LeadSourcesTab = ({ from, till, objectId }) => {
  const axiosPrivate = useAxiosPrivate();
  const params = useParams(from, till, objectId);
  const { data, isFetching } = useQuery({
    queryKey: ['bi/leadsrc', params],
    queryFn: async () =>
      (await axiosPrivate.get(`/boss/bi/lead-sources?${params}`)).data.data,
    keepPreviousData: true,
    retry: false,
  });
  const sources = data?.sources || [];
  return (
    <div>
      {isFetching && <Loading />}
      <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
        <Card title="Manba bo'yicha lidlar">
          {sources.length ? (
            <BarChart
              chartLabels={sources.map((s) => s.source)}
              chartDatasets={[
                {
                  label: 'Lidlar',
                  data: sources.map((s) => Number(s.leads) || 0),
                  backgroundColor: '#0057FF',
                  borderRadius: 4,
                },
                {
                  label: 'Shartnoma',
                  data: sources.map((s) => Number(s.won) || 0),
                  backgroundColor: '#16a34a',
                  borderRadius: 4,
                },
              ]}
              optionPlugins={{
                legend: {
                  display: true,
                  position: 'bottom',
                  labels: { usePointStyle: true, pointStyle: 'rectRounded' },
                },
              }}
              options={{ scales: { y: { beginAtZero: true } } }}
            />
          ) : (
            <Empty />
          )}
        </Card>
        <Card title='ROI jadval'>
          <SimpleTable
            head={['Manba', 'Lidlar', 'Shartnoma', 'Konversiya']}
            rows={sources.map((s) => ({
              cells: [s.source, s.leads, s.won, s.conversion + '%'],
            }))}
          />
        </Card>
      </div>
    </div>
  );
};

// ═══════════ 5) PROGNOZ ═══════════
const ForecastTab = ({ from, till, objectId }) => {
  const axiosPrivate = useAxiosPrivate();
  const params = useParams(from, till, objectId);
  const { data, isFetching } = useQuery({
    queryKey: ['bi/forecast', params],
    queryFn: async () =>
      (await axiosPrivate.get(`/boss/bi/forecast?${params}`)).data.data,
    keepPreviousData: true,
    retry: false,
  });
  return (
    <div>
      {isFetching && <Loading />}
      <Card title="Sotilish prognozi (oxirgi 90 kun tempi bo'yicha)">
        <SimpleTable
          head={[
            'Obyekt',
            'Jami',
            'Sotilgan',
            'Qolgan',
            'Kunlik temp',
            'Qolgan kun',
            'Tugash sanasi',
            'Progress',
          ]}
          rows={(data?.objects || []).map((o) => ({
            cells: [
              o.object_name,
              o.total_homes,
              o.sold,
              o.remaining,
              o.per_day,
              o.days_left ?? '—',
              o.sold_out_date || '—',
              o.progress + '%',
            ],
          }))}
        />
      </Card>
    </div>
  );
};

// ═══════════ 6) KUTILMALAR (to'lov/chiqim/solishtirish) ═══════════
const useExpectedPayments = (from, till, objectId) => {
  const axiosPrivate = useAxiosPrivate();
  const params = useParams(from, till, objectId);
  return useQuery({
    queryKey: ['bi/payments', params],
    queryFn: async () => (await axiosPrivate.get(`/boss/bi/payments?${params}`)).data.data,
    keepPreviousData: true,
    retry: false,
  });
};

const useExpenseForecast = () => {
  const axiosPrivate = useAxiosPrivate();
  return useQuery({
    queryKey: ['warehouse/finance/expense-forecast'],
    queryFn: async () =>
      (await axiosPrivate.get('/boss/warehouse/finance/expense-forecast')).data.data,
    retry: false,
  });
};

const useMonthlyExpenses = () => {
  const axiosPrivate = useAxiosPrivate();
  return useQuery({
    queryKey: ['warehouse/finance/monthly-expenses'],
    queryFn: async () =>
      (await axiosPrivate.get('/boss/warehouse/finance/monthly-expenses')).data.data,
    retry: false,
  });
};

const useComingExpenses = () => {
  const axiosPrivate = useAxiosPrivate();
  return useQuery({
    queryKey: ['warehouse/finance/coming-expenses'],
    queryFn: async () =>
      (await axiosPrivate.get('/boss/warehouse/finance/coming-expenses')).data.data,
    retry: false,
  });
};

const BudgetTab = ({ from, till, objectId }) => {
  const [sub, setSub] = useState('income');
  return (
    <div>
      <Tabs
        value={sub}
        onChange={(_, v) => setSub(v)}
        className='mb-3 border-b border-line'
      >
        <Tab value='income' label="Kutilayotgan to'lovlar" />
        <Tab value='expense' label='Kutilayotgan chiqimlar' />
        <Tab value='compare' label='Solishtirish' />
      </Tabs>
      {sub === 'income' && (
        <BudgetIncomeTab from={from} till={till} objectId={objectId} />
      )}
      {sub === 'expense' && <BudgetExpenseTab />}
      {sub === 'compare' && (
        <BudgetCompareTab from={from} till={till} objectId={objectId} />
      )}
    </div>
  );
};

// 6.1) Kutilayotgan to'lovlar — faqat oylik (bosh to'lovsiz), /boss/bi/payments
const BudgetIncomeTab = ({ from, till, objectId }) => {
  const { data, isFetching } = useExpectedPayments(from, till, objectId);
  const expected = data?.expected || [];
  const labels = expected.map((e) =>
    e.ym ? moment(e.ym, 'YYYY-MM').format('MM.YYYY') : e.label,
  );
  const hasValute = expected.some((e) => Number(e.valute) > 0);

  return (
    <div>
      {isFetching && <Loading />}
      <Card title="Oylik kutilayotgan to'lovlar">
        {expected.length ? (
          <BarChart
            chartLabels={labels}
            chartDatasets={[
              {
                label: "Summa (so'm)",
                data: expected.map((e) => Number(e.sum) || 0),
                backgroundColor: '#0057FF',
                borderRadius: 4,
              },
              ...(hasValute
                ? [
                    {
                      label: 'Summa ($)',
                      data: expected.map((e) => Number(e.valute) || 0),
                      backgroundColor: '#16a34a',
                      borderRadius: 4,
                    },
                  ]
                : []),
            ]}
            optionPlugins={{
              legend: {
                display: hasValute,
                position: 'bottom',
                labels: { usePointStyle: true },
              },
            }}
            options={{ scales: { y: { beginAtZero: true } } }}
          />
        ) : (
          <Empty />
        )}
      </Card>

      <Card title='Oylar jadvali' className='mt-4'>
        <SimpleTable
          head={['Oy', "Summa (so'm)", 'Summa ($)']}
          rows={expected.map((e) => ({
            cells: [
              e.ym ? moment(e.ym, 'YYYY-MM').format('MM.YYYY') : e.label,
              money(e.sum),
              Number(e.valute) > 0 ? money(e.valute) : '—',
            ],
          }))}
        />
      </Card>
    </div>
  );
};

// 6.2) Kutilayotgan chiqimlar — doimiy oylik + bir martalik rejadagi xarajatlar
const BudgetExpenseTab = () => {
  const { data: forecast, isFetching: fFetch } = useExpenseForecast();
  const { data: monthly, isFetching: mFetch } = useMonthlyExpenses();
  const { data: coming, isFetching: cFetch } = useComingExpenses();

  const history = forecast?.history || [];
  const bars = [
    ...history,
    {
      ym: forecast?.month,
      sum: Number(forecast?.planned_total?.sum) || 0,
    },
  ];

  return (
    <div>
      {(fFetch || mFetch || cFetch) && <Loading />}

      <Card title='Xarajat — oxirgi oylar va kelasi oy prognozi'>
        {bars.some((b) => Number(b.sum) > 0) ? (
          <BarChart
            chartLabels={bars.map((b) =>
              b.ym ? moment(b.ym, 'YYYY-MM').format('MM.YYYY') : '—',
            )}
            chartDatasets={[
              {
                label: "Xarajat (so'm)",
                data: bars.map((b) => Number(b.sum) || 0),
                backgroundColor: bars.map((_, i) =>
                  i === bars.length - 1 ? '#dc2626' : '#94a3b8',
                ),
                borderRadius: 4,
              },
            ]}
            optionPlugins={{ legend: { display: false } }}
            options={{ scales: { y: { beginAtZero: true } } }}
          />
        ) : (
          <Empty />
        )}
      </Card>

      <div className='grid grid-cols-2 lg:grid-cols-3 gap-3 my-4'>
        <Stat label='Doimiy oylik' value={money(forecast?.monthly_total?.sum)} />
        <Stat label='Bir martalik' value={money(forecast?.coming_total?.sum)} />
        <Stat label='Jami (kelasi oy)' value={money(forecast?.planned_total?.sum)} />
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
        <Card title='Doimiy oylik xarajatlar'>
          <SimpleTable
            head={['Nomi', 'Izoh', 'Summa']}
            rows={(monthly?.items || []).map((m) => ({
              cells: [
                m.name,
                m.comment || '—',
                <MoneyCell uzs={m.sum} usd={m.valute} />,
              ],
            }))}
          />
        </Card>
        <Card title='Bir martalik rejadagi xarajatlar'>
          <SimpleTable
            head={['Sana', 'Sabab', 'Summa']}
            rows={(coming?.items || []).map((c) => ({
              cells: [
                moment(c.date).format('DD.MM.YYYY'),
                c.reason?.name || '—',
                <MoneyCell uzs={c.sum} usd={c.valute} />,
              ],
            }))}
          />
        </Card>
      </div>
    </div>
  );
};

// 6.3) Solishtirish — kelasi oy tushum vs xarajat (jadvalsiz)
const BudgetCompareTab = ({ from, till, objectId }) => {
  const { data: incomeData, isFetching: iFetch } = useExpectedPayments(
    from,
    till,
    objectId,
  );
  const { data: forecast, isFetching: fFetch } = useExpenseForecast();

  const expected = incomeData?.expected || [];
  const now = moment();
  const nextMonth = [...expected]
    .filter((e) => e.ym && moment(e.ym, 'YYYY-MM').isAfter(now, 'month'))
    .sort((a, b) => (a.ym < b.ym ? -1 : 1))[0];

  const incomeSum = Number(nextMonth?.sum) || 0;
  const expenseSum = Number(forecast?.planned_total?.sum) || 0;

  return (
    <div>
      {(iFetch || fFetch) && <Loading />}
      <div className='grid grid-cols-2 gap-3 mb-4'>
        <Stat
          label='Kelasi oy — kutilayotgan tushum'
          value={money(incomeSum) + " so'm"}
        />
        <Stat
          label='Kelasi oy — rejadagi xarajat'
          value={money(expenseSum) + " so'm"}
        />
      </div>
      <Card title='Kelasi oy — tushum va xarajat solishtirmasi'>
        <BarChart
          chartLabels={['Tushum', 'Xarajat']}
          chartDatasets={[
            {
              label: 'Summa',
              data: [incomeSum, expenseSum],
              backgroundColor: ['#16a34a', '#dc2626'],
              borderRadius: 4,
            },
          ]}
          optionPlugins={{ legend: { display: false } }}
          options={{ scales: { y: { beginAtZero: true } } }}
        />
      </Card>
    </div>
  );
};

// ── umumiy kichik komponentlar ──
const Stat = ({ label, value }) => (
  <div className='bg-white border border-line rounded-lg px-4 py-3'>
    <div className='text-[12px] text-muted mb-0.5'>{label}</div>
    <div className='text-[18px] font-semibold text-ink tabular-nums'>{value}</div>
  </div>
);

const Card = ({ title, children, className = '' }) => (
  <div className={`bg-white border border-line rounded-lg p-4 ${className}`}>
    <div className='font-semibold mb-2 text-sm'>{title}</div>
    <div className='min-h-[280px]'>{children}</div>
  </div>
);

// Ikki valyutali summa katagi — har valyuta o'z qatorida, qisqarmaydi.
// Bitta qatorga " · " bilan qo'shilganda uzun matn ustunlarni buzib yuborardi.
const MoneyCell = ({ uzs, usd }) => {
  const parts = [
    Number(uzs) > 0 ? `${money(uzs)} so'm` : null,
    Number(usd) > 0 ? `$${money(usd)}` : null,
  ].filter(Boolean);
  if (!parts.length) return '—';
  return (
    <div className='flex flex-col items-end leading-tight'>
      {parts.map((p) => (
        <span key={p} className='whitespace-nowrap'>
          {p}
        </span>
      ))}
    </div>
  );
};

const SimpleTable = ({ head = [], rows = [] }) =>
  rows.length ? (
    <div className='overflow-x-auto'>
      <table className='w-full text-sm'>
        <thead>
          <tr className='text-muted border-b border-line'>
            {head.map((h, i) => (
              <th
                key={i}
                className={`whitespace-nowrap ${
                  i === 0 ? 'text-left py-1.5' : 'text-right pl-4'
                }`}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr
              key={i}
              className={`border-b border-gray-100 ${
                r.onClick ? 'cursor-pointer hover:bg-gray-50' : ''
              }`}
              onClick={r.onClick || undefined}
            >
              {r.cells.map((c, j) => (
                <td
                  key={j}
                  className={
                    j === 0 ? 'py-1.5 whitespace-nowrap' : 'text-right pl-4'
                  }
                >
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ) : (
    <Empty />
  );

const Loading = () => (
  <div className='flex items-center gap-2 text-sm text-gray-500 mb-2'>
    <CircularProgress size={16} /> Yuklanmoqda…
  </div>
);

const Empty = ({ text = "Ma'lumot yo'q" }) => (
  <div className='h-[240px] flex items-center justify-center text-gray-400 text-sm'>
    <i className='bi bi-exclamation-octagon mr-1' /> {text}
  </div>
);

export default memo(BiAnalytic);
